import React, { useState, useCallback, useEffect } from 'react';
import PropTypes, { object } from 'prop-types';
import {
  sortableContainer,
  sortableElement,
  sortableHandle
} from 'react-sortable-hoc';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import isEqual from 'lodash/isEqual';

import {
  ButtonWithDropdown,
  Checkbox,
  Icon,
  ButtonIcon
} from 'components/common/Elements/Elements';

import InputWithEnter from 'components/common/forms/InputWithEnter/InputWithEnter';

import InputWithUnmaunt from 'components/common/forms/InputWithUnmaunt/InputWithUnmaunt';
import {
  validationNumberAndLetters,
  validationNoSpecialCharacters
} from 'utils/validation';

import { moveIndex } from 'utils/common';
import { showNotif } from 'utils/notification';

import classes from './TableColumnSetting.module.scss';

const DragHandle = sortableHandle(({ find }) => (
  <Icon
    size="16px"
    className={cn(classes.hover, find !== '' && classes.hidden)}
    icon="drugDrop"
  />
));

const SortableItem = sortableElement(
  ({ value, labelChange, checked, find, disabledMode }) => (
    <li className={classes.list_items}>
      <DragHandle find={find} />
      <Checkbox
        value={value}
        onChange={labelChange}
        checked={checked}
        disabledMode={disabledMode}
      />
    </li>
  )
);

const SortableContainer = sortableContainer(({ children }) => {
  return <ul className={classes.list}>{children}</ul>;
});

const TableColumnSetting = ({
  tableConf = [],
  setTableConf,
  setVisibleFilterSave,
  setVisibleFilterEdit,
  changeFilter,
  visibleFilterSave,
  visibleFilterEdit,
  setTriggerSaveTableSettings,
  triggerSaveTableSettings,
  defaulTableConf = [],
  position = 'custom',
  setFilterButton
}) => {
  const [find, setFind] = useState('');
  const [markerEditTableSetting, serMarkerEditTableSetting] = useState(false);
  const [isDisabled, setIsdisabled] = useState(false);
  const [outsideClose, setOutsideClose] = useState(false);

  const { t } = useTranslation(['activity']);

  useEffect(() => {
    const newConfig = tableConf.map(el => {
      return { field: el.field, show: el.show, noAccess: el.noAccess };
    });

    const oldConfig = defaulTableConf.map(el => {
      return { field: el.field, show: el.show, noAccess: el.noAccess };
    });

    if (!isEqual(newConfig, oldConfig)) {
      serMarkerEditTableSetting(true);
    } else {
      serMarkerEditTableSetting(false);
    }
    let countChecked = 0;
    newConfig.forEach(item => {
      if (item.show && !item.noAccess) {
        countChecked += 1;
      }
    });
    setIsdisabled(countChecked <= 5 ? true : false);
  }, [tableConf, defaulTableConf]);

  const labelChange = e => {
    if (!isDisabled || e.target.checked === true) {
      const newArr = [...tableConf];
      const ind = tableConf.findIndex(el => el.title === e.target.value);
      newArr[ind].show = e.target.checked;
      setTableConf(newArr);
      setVisibleFilterSave('button');
      if (changeFilter !== 'Default') {
        setVisibleFilterEdit(true);
      }
    } else {
      showNotif({
        message: `The number of columns cannot be less than five`,
        kind: 'error',
        dismissAfter: 5000,
        type: 'alert'
      });
    }
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const newArr = [...tableConf.slice(1)];
    moveIndex(newArr, oldIndex, newIndex);
    setTableConf([tableConf[0], ...newArr]);
    setVisibleFilterSave('button');
    if (changeFilter !== 'Default') {
      setVisibleFilterEdit(true);
    }
  };

  const handleFind = e => {
    if (
      (validationNumberAndLetters(e.target.value.slice(-1)) &&
        `${find}${e.target.value.slice(-1)}` !== ' ') ||
      e.target.value === ''
    ) {
      setFind(e.target.value);
    }
  };

  const onClearFind = useCallback(() => {
    setFind('');
  }, []);

  const withFilter = (tableConf, find) => {
    if (find === '') {
      return tableConf[tableConf.length - 1].title
        ? tableConf.slice(1)
        : tableConf.slice(1, -1);
    } else {
      return [
        ...tableConf.filter(
          item =>
            item.title.toLowerCase().includes(find.toLowerCase()) &&
            item.title !== tableConf[0].title
        )
      ];
    }
  };

  const onSaveFilter = e => {
    setTriggerSaveTableSettings(e);
    if (setFilterButton) {
      setFilterButton(false);
    }
    setOutsideClose(!outsideClose);
  };

  return (
    <div className={classes.wrapBtnSetting}>
      <ButtonWithDropdown
        className={cn(
          classes.btnSetting,
          markerEditTableSetting && classes.btnSetting_marker
        )}
        position={position}
        positionCustomClass={classes.position}
        outsideClose={outsideClose}
      >
        <>
          <InputWithUnmaunt
            placeholder={t('activity:filters:COLUMN_NAME')}
            value={find}
            onChange={handleFind}
            onClearSearch={onClearFind}
            unmauntFunc={onClearFind}
            className={classes.input}
            validator={validationNoSpecialCharacters}
          />
          <SortableContainer onSortEnd={onSortEnd} useDragHandle>
            {withFilter(tableConf, find).map(
              (value, index) =>
                !value.noAccess && (
                  <SortableItem
                    disabled={find === '' ? false : true}
                    key={`item-${value.title}`}
                    index={index}
                    value={value.title}
                    labelChange={labelChange}
                    checked={value.show}
                    disabledMode={isDisabled}
                    find={find}
                  />
                )
            )}
          </SortableContainer>
          {visibleFilterSave === 'button' && (
            <ButtonIcon
              className={classes.save}
              icon="save"
              buttonText={t('activity:filters.SAVE_VIEW_AS')}
              onClick={() => {
                setVisibleFilterSave('input');
                setVisibleFilterEdit(false);
              }}
            />
          )}
          {visibleFilterEdit && (
            <ButtonIcon
              className={classes.save}
              icon="update"
              buttonText={t('activity:filters.SAVE_VIEW')}
              onClick={() => {
                setTriggerSaveTableSettings(!triggerSaveTableSettings);
                setOutsideClose(!outsideClose);
                if (setFilterButton) {
                  setFilterButton(false);
                }
              }}
            />
          )}
          {visibleFilterSave === 'input' && (
            <InputWithEnter
              className={classes.inputSave}
              placeholder={t('activity:filters.ENTER_VIEW_NAME')}
              iconSearch={false}
              handleSubmit={onSaveFilter}
            />
          )}
        </>
      </ButtonWithDropdown>
    </div>
  );
};

TableColumnSetting.propTypes = {
  tableConf: PropTypes.arrayOf(object),
  setTableConf: PropTypes.func,
  setVisibleFilterSave: PropTypes.func,
  setVisibleFilterEdit: PropTypes.func,
  changeFilter: PropTypes.string,
  visibleFilterSave: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  visibleFilterEdit: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  setTriggerSaveTableSettings: PropTypes.func,
  triggerSaveTableSettings: PropTypes.bool,
  defaulTableConf: PropTypes.array,
  position: PropTypes.string,
  setFilterButton: PropTypes.func
};

export default TableColumnSetting;
